if (!customElements.get('wls-slider')) {
  class WLSSlider extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        slider: 'js-wls-slider',
        nextButton: 'js-wls-slider-next',
        prevButton: 'js-wls-slider-prev',
        sliderSettings: `SliderSettings-${this.getAttribute('data-id') || 'default'}`,
        pagination: 'js-wls-slider-pagination',
        progressbar: 'js-wls-slider-progressbar',
        navigation: 'js-slider-navigation',
        swiperSlide: 'swiper-slide',
        video: 'wls-video',
        playing: 'playing',
        hidden: 'hidden',
        locked: 'wls-slider--locked',
        fallback: 'wls-slider--fallback',
        thumbnailSlider: 'js-wls-thumbnail-slider',
      };

      this.swiperInstance = null;
      this.thumbnailSwiperInstance = null;
      this.resizeTimeout = null;

      this.boundHandleResize = this._handleResize.bind(this);
      this.boundReInit = this.reInit.bind(this);
    }

    /* --------------------------------------------------------------------------
     * DOM
     * -------------------------------------------------------------------------- */

    get elements() {
      return {
        slider: this.querySelector(`.${this.classes.slider}`),
        nextButtons: this.querySelectorAll(`.${this.classes.nextButton}`),
        prevButtons: this.querySelectorAll(`.${this.classes.prevButton}`),
        paginations: this.querySelectorAll(`.${this.classes.pagination}`),
        navigations: this.querySelectorAll(`.${this.classes.navigation}`),
        progressbars: this.querySelectorAll(`.${this.classes.progressbar}`),
        thumbnailSlider: this.querySelector(`.${this.classes.thumbnailSlider}`),
      };
    }

    /* --------------------------------------------------------------------------
     * LIFECYCLE
     * -------------------------------------------------------------------------- */

    connectedCallback() {
      this._init();
      window.addEventListener('resize', this.boundHandleResize);
      this.addEventListener('wls:slider:reinit', this.boundReInit);
    }

    disconnectedCallback() {
      clearTimeout(this.resizeTimeout);
      this._destroy();

      window.removeEventListener('resize', this.boundHandleResize);
      this.removeEventListener('wls:slider:reinit', this.boundReInit);
    }

    reInit() {
      this._destroy();
      this._init();
    }

    /* --------------------------------------------------------------------------
     * INIT
     * -------------------------------------------------------------------------- */

    _init() {
      if (typeof Swiper === 'undefined') return;
      if (!this.elements.slider) return;

      const totalSlides =
        this.elements.slider.querySelectorAll(`.${this.classes.swiperSlide}`).length;

      if (!totalSlides) return;

      const {
        slidesPerView,
        spaceBetween,
        centeredSlides,
        navigation,
        pagination,
        autoplay,
        insufficientSlidesPosition,
        loop,
        thumbnails,
        customSettings,
      } = this._getSliderSettingsFromLiquid();

      // Disable centeredSlides when thumbnails are enabled (Swiper limitation)
      if (thumbnails?.enabled) {
        centeredSlides.base = false;
        centeredSlides.tablet = false;
        centeredSlides.desktop = false;
      }

      if (this._shouldUseFallbackLayout(slidesPerView, totalSlides)) {
        this._applyFallbackLayout(slidesPerView, spaceBetween);
        return;
      }

      const hasThumbnails =
        thumbnails?.enabled &&
        this.elements.thumbnailSlider &&
        this.elements.thumbnailSlider.querySelectorAll('.swiper-slide').length > 0;

      if (hasThumbnails) {
        this._initThumbnailSlider(thumbnails);
      }

      this.swiperInstance = new Swiper(this.elements.slider, {
        slidesPerView: slidesPerView.base,
        spaceBetween: spaceBetween.base,
        loop: loop?.base || false,
        initialSlide: 0,
        centeredSlides: centeredSlides.base,
        centerInsufficientSlides: false,

        navigation: {
          nextEl: [...this.elements.nextButtons],
          prevEl: [...this.elements.prevButtons],
          enabled: navigation.base,
        },

        pagination: {
          el: [...this.elements.paginations],
          clickable: true,
          enabled: pagination.base,
          type: pagination.type,
        },

        autoplay: autoplay.base
          ? { delay: autoplay.time, disableOnInteraction: false }
          : false,

        thumbs: hasThumbnails
          ? { swiper: this.thumbnailSwiperInstance }
          : undefined,

        grabCursor: true,
        ...customSettings,

        on: {
          init: () => {
            this.elements.navigations.forEach(nav =>
              nav.classList.remove(this.classes.hidden)
            );
            // Force slider to start at position 0
            this.swiperInstance.slideTo(0, 0);
            this._updateProgressbar();
          },
          slideChange: () => {
            this._handleSlideChange();
          },
          lock: () => this.classList.add(this.classes.locked),
          unlock: () => this.classList.remove(this.classes.locked),
        },

        breakpoints: {
          768: {
            slidesPerView: slidesPerView.tablet,
            spaceBetween: spaceBetween.tablet,
            centeredSlides: centeredSlides.tablet,
            loop: loop?.tablet || false,
            centerInsufficientSlides: false,
            navigation: { enabled: navigation.tablet },
            pagination: { enabled: pagination.tablet },
          },
          1024: {
            slidesPerView: slidesPerView.desktop,
            spaceBetween: spaceBetween.desktop,
            centeredSlides: centeredSlides.desktop,
            loop: loop?.desktop || false,
            centerInsufficientSlides: false,
            navigation: { enabled: navigation.desktop },
            pagination: { enabled: pagination.desktop },
          },
        },
      });
    }

    /* --------------------------------------------------------------------------
     * THUMBNAILS
     * -------------------------------------------------------------------------- */

    _initThumbnailSlider(config) {
      this.thumbnailSwiperInstance = new Swiper(
        this.elements.thumbnailSlider,
        {
          slidesPerView: config.slidesPerView,
          spaceBetween: config.spaceBetween,
          freeMode: config.freeMode,
          watchSlidesProgress: true,
          slideToClickedSlide: true,

          breakpoints: {
            768: {
              slidesPerView: config.slidesPerViewTablet,
              spaceBetween: config.spaceBetweenTablet,
            },
            1024: {
              slidesPerView: config.slidesPerViewDesktop,
              spaceBetween: config.spaceBetweenDesktop,
            },
          },
        }
      );
    }

    /* --------------------------------------------------------------------------
     * EVENTS
     * -------------------------------------------------------------------------- */

    _handleSlideChange() {
      this.querySelectorAll(`.${this.classes.video}`).forEach(el => {
        const video = el.querySelector('video');
        if (video && !video.paused) {
          video.pause();
          el.classList.remove(this.classes.playing);
        }
      });

      this._updateProgressbar();
    }

    _handleResize() {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.reInit();
        // Ensure slider resets to position 0 after resize
        if (this.swiperInstance) {
          this.swiperInstance.slideTo(0, 0);
        }
      }, 150);
    }

    /* --------------------------------------------------------------------------
     * FALLBACK
     * -------------------------------------------------------------------------- */

    _applyFallbackLayout(slidesPerView, spaceBetween) {
      let spv = slidesPerView.base;
      let gap = spaceBetween.base;

      if (window.innerWidth >= 1024) {
        spv = slidesPerView.desktop;
        gap = spaceBetween.desktop;
      } else if (window.innerWidth >= 768) {
        spv = slidesPerView.tablet;
        gap = spaceBetween.tablet;
      }

      this.classList.add(this.classes.fallback);
      this.style.setProperty('--fallback-slides-per-view', spv);
      this.style.setProperty('--fallback-gap', `${gap}px`);

      this.elements.navigations.forEach(nav =>
        nav.classList.add(this.classes.hidden)
      );
    }

    _shouldUseFallbackLayout(spv, total) {
      let val = spv.base;
      if (window.innerWidth >= 1024) val = spv.desktop;
      else if (window.innerWidth >= 768) val = spv.tablet;
      return total <= val;
    }

    /* --------------------------------------------------------------------------
     * DESTROY
     * -------------------------------------------------------------------------- */

    _destroy() {
      if (this.thumbnailSwiperInstance) {
        this.thumbnailSwiperInstance.destroy(true, true);
        this.thumbnailSwiperInstance = null;
      }

      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
      }
    }

    /* --------------------------------------------------------------------------
     * PROGRESS
     * -------------------------------------------------------------------------- */

    _updateProgressbar() {
      if (!this.swiperInstance) return;

      const total = this.swiperInstance.slides.length;
      const index = this.swiperInstance.activeIndex + 1;
      const percent = (index / total) * 100;

      this.elements.progressbars.forEach(bar => {
        bar.style.width = `${percent}%`;
      });
    }

    /* --------------------------------------------------------------------------
     * SETTINGS
     * -------------------------------------------------------------------------- */

    _getDefaultSettings() {
      return {
        slidesPerView: { base: 1, tablet: 1, desktop: 1 },
        spaceBetween: { base: 16, tablet: 16, desktop: 24 },
        centeredSlides: { base: false, tablet: false, desktop: false },
        navigation: { base: false, tablet: false, desktop: false },
        pagination: {
          base: false,
          tablet: false,
          desktop: false,
          type: 'bullets',
        },
        autoplay: { base: false, time: 3000 },
        loop: { base: false, tablet: false, desktop: false },
        insufficientSlidesPosition: {
          base: 'start',
          tablet: 'start',
          desktop: 'start',
        },
        customSettings: {},
        thumbnails: {
          enabled: false,
          slidesPerView: 4,
          slidesPerViewTablet: 4,
          slidesPerViewDesktop: 6,
          spaceBetween: 8,
          spaceBetweenTablet: 8,
          spaceBetweenDesktop: 8,
          freeMode: true,
        },
      };
    }

    _getSliderSettingsFromLiquid() {
      const defaults = this._getDefaultSettings();
      const el = document.getElementById(this.classes.sliderSettings);
      if (!el) return defaults;

      try {
        const json = JSON.parse(el.textContent.trim());
        return {
          ...defaults,
          ...json,
          slidesPerView: { ...defaults.slidesPerView, ...json.slidesPerView },
          spaceBetween: { ...defaults.spaceBetween, ...json.spaceBetween },
          centeredSlides: { ...defaults.centeredSlides, ...json.centeredSlides },
          navigation: { ...defaults.navigation, ...json.navigation },
          pagination: { ...defaults.pagination, ...json.pagination },
          autoplay: { ...defaults.autoplay, ...json.autoplay },
          loop: { ...defaults.loop, ...json.loop },
          insufficientSlidesPosition: { ...defaults.insufficientSlidesPosition, ...json.insufficientSlidesPosition },
          thumbnails: { ...defaults.thumbnails, ...json.thumbnails },
        };
      } catch {
        return defaults;
      }
    }
  }

  customElements.define('wls-slider', WLSSlider);
}